import { Fragment, type ReactNode } from "react";

/**
 * renderMarkdown — the XSS boundary for user-authored post bodies (S1d).
 *
 * Post `content` is stored RAW and rendered client-side. This parser turns a SMALL
 * safe markdown subset into REACT ELEMENTS — it NEVER builds an HTML string and
 * NEVER uses `dangerouslySetInnerHTML`. That is the whole reason it is safe:
 * untrusted source can only ever land as ESCAPED TEXT inside a React text node
 * (React escapes every string child), never as live DOM. A `<script>` / `<img
 * onerror>` / `<b>` in the source therefore shows as literal text, not markup.
 *
 * Supported subset (everything else -> literal escaped text):
 *   - **bold**                -> <strong>
 *   - *italic* / _italic_     -> <em>
 *   - `code` (inline)         -> <code>   (markdown INSIDE code stays literal)
 *   - [label](url)            -> <a target=_blank rel="noopener noreferrer">
 *   - -/* unordered list      -> <ul><li>
 *   - 1. ordered list         -> <ol><li>
 *   - blank line              -> paragraph break (<p> per block)
 *   - single newline          -> soft break (<br>)
 *
 * SECURITY contract:
 *   - href ALLOWLIST: only http(s) and relative `/...` survive. The scheme is
 *     matched AFTER stripping leading whitespace + ASCII control chars and AFTER
 *     removing control chars embedded before the colon (a real browser collapses
 *     `java\tscript:` -> `javascript:`), case-insensitively. A rejected url renders
 *     the link as INERT text (no live href).
 *   - markdown IMAGES `![alt](url)` are DISALLOWED -> they produce NO <img>/<svg>;
 *     the `![...](...)` renders as literal text (images go ONLY through the S1c
 *     typed media allowlist, never via the post body).
 *   - malformed input (unclosed `**`, empty `[]()`, lone marker, "") never throws.
 */
export function renderMarkdown(text: string): ReactNode {
  if (typeof text !== "string" || text.length === 0) {
    return null;
  }

  // Split into blocks on blank lines (one or more lines that are only whitespace).
  // Each block becomes either a list (<ul>/<ol>) or a paragraph (<p>).
  const blocks = splitBlocks(text);

  return (
    <>
      {blocks.map((block, i) => (
        <Fragment key={i}>{renderBlock(block)}</Fragment>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Block layer
// ---------------------------------------------------------------------------

/** Split the source into blocks separated by blank lines. Blank-only blocks are
 *  dropped (they carry no content). */
function splitBlocks(text: string): string[] {
  return text
    .split(/\n[ \t]*\n/)
    .map((b) => b.replace(/^\n+|\n+$/g, ""))
    .filter((b) => b.trim().length > 0);
}

const UNORDERED_RE = /^[ \t]*[-*][ \t]+(.*)$/;
const ORDERED_RE = /^[ \t]*\d+\.[ \t]+(.*)$/;

/** Render one block: an unordered list, an ordered list, or a paragraph. */
function renderBlock(block: string): ReactNode {
  const lines = block.split("\n");

  // A block is a list only when EVERY non-empty line is a list item of the SAME
  // kind. A single stray non-item line makes it a paragraph (so a lone "* " can't
  // masquerade as a one-item list when it is really prose).
  const nonEmpty = lines.filter((l) => l.trim().length > 0);
  const allUnordered =
    nonEmpty.length > 0 && nonEmpty.every((l) => UNORDERED_RE.test(l));
  const allOrdered =
    nonEmpty.length > 0 && nonEmpty.every((l) => ORDERED_RE.test(l));

  if (allUnordered) {
    return (
      <ul>
        {nonEmpty.map((line, i) => (
          <li key={i}>{renderInline(line.replace(UNORDERED_RE, "$1"))}</li>
        ))}
      </ul>
    );
  }

  if (allOrdered) {
    return (
      <ol>
        {nonEmpty.map((line, i) => (
          <li key={i}>{renderInline(line.replace(ORDERED_RE, "$1"))}</li>
        ))}
      </ol>
    );
  }

  // A paragraph: each source line is inline-parsed; single newlines become <br>.
  return (
    <p>
      {lines.map((line, i) => (
        <Fragment key={i}>
          {i > 0 ? <br /> : null}
          {renderInline(line)}
        </Fragment>
      ))}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Inline layer
// ---------------------------------------------------------------------------

// The SAFE inline constructs, each matched by a STICKY (`y`) regex that is anchored
// at the scan cursor — it tries to match ONLY at the current index, never scanning
// forward. That is what keeps tokenization LINEAR: a `[` that does not begin a
// complete `[label](url)` fails the sticky link rule in O(1) at that position, and
// the scanner emits the bare `[` and advances by ONE — it never re-scans the suffix
// hunting for a `]`/`)` that closes a later opener (the old `/g` exec loop did, which
// made a body of many `[` or `[a](` fragments O(n²) — a bounded DoS, #1445).
//
// Ordering / precedence at a position (first match wins):
//   1. code      -- `` `code` `` (so markdown inside code stays literal).
//   2. link      -- `[label](url)`, but NOT an image `![...]` (guarded at the call
//                   site by checking the preceding char, since `y` has no lookbehind
//                   on older engines — see the scanner).
//   3. bold      -- `**text**` before single-`*` italic.
//   4. italic    -- `*text*` (asterisk) / `_text_` (underscore, word-bounded).
// Anything NOT matched at a position is a literal char (React escapes it -> inert).
//
// Underscore emphasis stays INTRAWORD-SAFE (the CommonMark rule): `_text_` is italic
// ONLY when both delimiters sit at a word boundary. The scanner enforces the LEFT
// boundary (the char before `_` is not an ASCII word char) and the sticky underscore
// rule's trailing `(?![A-Za-z0-9])` enforces the RIGHT boundary. This stops an
// identifier like `window.__xss_feed = 1` (or any `foo_bar_baz`) from being mangled
// into an <em>: the body must render VERBATIM (a security regression guarantee).
//
// Per-token inner-scan CAP. The `[^X]` runs below are length-bounded with `{1,N}` /
// `{0,N}` rather than unbounded `+`/`*`. This (with the link `indexOf` fast-fail in
// the scanner) is the OTHER half of the linearization (#1445): an UNCLOSED opener
// (`[a](` with no `)`, or a lone `[` followed by 50k chars) would otherwise make the
// sticky regex scan the whole remaining suffix at EVERY opener — still O(n²). Capping
// the inner run makes each anchored attempt O(CAP) = O(1), so the full scan is O(n).
// 512 is far longer than any real inline label / url / emphasis run (the backend caps
// a WHOLE post body at 5000 chars), so no legitimate construct is truncated; a
// construct longer than the cap simply does not match and renders as literal text
// (safe — never a live anchor). Measured worst cases: 32k `[` ≈ 17ms, 16k `[a](`
// ≈ 25ms — well under the security suite's 100ms bound, with comfortable CI headroom.
const INLINE_CAP = 512;
const C = String(INLINE_CAP);

const CODE_Y = new RegExp("`[^`]{1," + C + "}`", "y"); // 1: inline code
// 2: link (image-ness checked by the scanner). Label + url each capped.
const LINK_Y = new RegExp(
  "\\[[^\\]]{0," + C + "}\\]\\([^)]{0," + C + "}\\)",
  "y",
);
const BOLD_Y = new RegExp("\\*\\*[^*]{1," + C + "}\\*\\*", "y"); // 3: bold
const ITALIC_STAR_Y = new RegExp("\\*[^*]{1," + C + "}\\*", "y"); // 4a: italic (asterisk)
// 4b: italic (underscore, word-bounded — right boundary in the pattern).
const UNDERSCORE_Y = new RegExp("_[^_]{1," + C + "}_(?![A-Za-z0-9])", "y");

const LINK_RE = /^\[([^\]]*)\]\(([^)]*)\)$/;

/** True when `ch` is an ASCII word char (the underscore left-boundary test). */
function isWordChar(ch: string | undefined): boolean {
  if (ch === undefined) return false;
  return (
    (ch >= "0" && ch <= "9") ||
    (ch >= "A" && ch <= "Z") ||
    (ch >= "a" && ch <= "z")
  );
}

/**
 * Try every inline rule at `pos` (sticky/anchored — O(1) at this index). Returns the
 * matched token text, or null if no construct starts here. Precedence matches the old
 * alternation: code → link → bold → italic-star → italic-underscore.
 */
function matchInlineAt(line: string, pos: number): string | null {
  const ch = line[pos];
  // Fast reject: only these four chars can BEGIN a construct. Anything else is a
  // literal — this is the common case for ordinary prose, so check it first.
  if (ch !== "`" && ch !== "[" && ch !== "*" && ch !== "_") return null;

  if (ch === "`") {
    CODE_Y.lastIndex = pos;
    const m = CODE_Y.exec(line);
    return m ? m[0] : null;
  }

  if (ch === "[") {
    // An image `![...]` must NOT become a link: the whole `![...](...)` falls
    // through to literal text. `y` regexes can't use a lookbehind portably, so the
    // scanner checks the preceding char here instead of in the pattern.
    if (pos > 0 && line[pos - 1] === "!") return null;
    // Fast-fail (#1445): a link needs a closing `]` within CAP chars. With NO `]`
    // ahead (a run of bare `[`), `indexOf` returns -1 in one native scan and we skip
    // the regex entirely — otherwise the capped regex would still scan CAP chars
    // forward at every `[`. This is what makes the all-`[` body genuinely linear.
    const close = line.indexOf("]", pos + 1);
    if (close === -1 || close - pos > INLINE_CAP) return null;
    LINK_Y.lastIndex = pos;
    const m = LINK_Y.exec(line);
    return m ? m[0] : null;
  }

  if (ch === "*") {
    // Bold before italic (longest meaningful first), both anchored at `pos`.
    BOLD_Y.lastIndex = pos;
    const b = BOLD_Y.exec(line);
    if (b) return b[0];
    ITALIC_STAR_Y.lastIndex = pos;
    const i = ITALIC_STAR_Y.exec(line);
    return i ? i[0] : null;
  }

  // ch === "_": underscore italic only at a LEFT word boundary (CommonMark intraword
  // rule); the pattern enforces the RIGHT boundary.
  if (isWordChar(line[pos - 1])) return null;
  UNDERSCORE_Y.lastIndex = pos;
  const u = UNDERSCORE_Y.exec(line);
  return u ? u[0] : null;
}

/**
 * Parse a single line of text into inline React nodes.
 *
 * LINEAR single forward scan: walk `i` from 0 to length, trying the anchored inline
 * rules ONLY at `i`. On a match, flush any pending literal run, push the token, and
 * jump past it. On no match, the char at `i` is literal — extend the pending run and
 * advance by ONE. Each index is visited once and each rule is tried at most once per
 * index, so the whole pass is O(n) (no quadratic re-scan, #1445).
 */
function renderInline(line: string): ReactNode {
  if (line.length === 0) return null;

  const nodes: ReactNode[] = [];
  let key = 0;
  let runStart = 0; // start of the current pending literal run
  let i = 0;

  while (i < line.length) {
    const token = matchInlineAt(line, i);
    if (token === null) {
      // Literal char — keep it in the pending run, advance by one.
      i += 1;
      continue;
    }
    // Flush the literal run before this token (React escapes it -> inert).
    if (i > runStart) nodes.push(line.slice(runStart, i));
    nodes.push(renderToken(token, key++));
    i += token.length;
    runStart = i;
  }

  // Trailing literal text.
  if (runStart < line.length) nodes.push(line.slice(runStart));

  // A single string child is fine to return as-is.
  if (nodes.length === 1 && typeof nodes[0] === "string") return nodes[0];
  return nodes;
}

/** Turn a single matched inline token into the corresponding safe element. */
function renderToken(token: string, key: number): ReactNode {
  // Inline code: `code` -- content stays LITERAL (no nested markdown parsing).
  if (token.startsWith("`") && token.endsWith("`")) {
    return <code key={key}>{token.slice(1, -1)}</code>;
  }

  // Bold: **text**
  if (token.startsWith("**") && token.endsWith("**")) {
    return <strong key={key}>{token.slice(2, -2)}</strong>;
  }

  // Italic: *text* or _text_
  if (
    (token.startsWith("*") && token.endsWith("*")) ||
    (token.startsWith("_") && token.endsWith("_"))
  ) {
    return <em key={key}>{token.slice(1, -1)}</em>;
  }

  // Link: [label](url)
  const link = LINK_RE.exec(token);
  if (link) {
    const label = link[1];
    const rawUrl = link[2];
    const href = sanitizeHref(rawUrl);
    // The visible text is ALWAYS the label (fall back to the raw url text only if
    // the label is empty, so an empty `[]()` still shows something readable).
    const visible = label.length > 0 ? label : rawUrl;
    if (href === null) {
      // Rejected scheme / empty url -> INERT text, never a live anchor.
      return <Fragment key={key}>{visible}</Fragment>;
    }
    return (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer">
        {visible}
      </a>
    );
  }

  // Unreachable for our alternation, but stay safe: render as literal text.
  return <Fragment key={key}>{token}</Fragment>;
}

// ---------------------------------------------------------------------------
// href sanitization -- the security core
// ---------------------------------------------------------------------------

// ASCII whitespace + ALL C0 control chars ( -), the space ( ), and
// DEL (). These are exactly the bytes a browser ignores/collapses inside a
// scheme: `java\tscript:` and `\njavascript:` both normalize to `javascript:`, so
// they must be stripped BEFORE the scheme is compared. Built via string escapes so
// the source contains no raw control bytes.
const LEADING_CONTROL_RE = new RegExp("^[\\u0000-\\u0020\\u007F]+");
const TRAILING_CONTROL_RE = new RegExp("[\\u0000-\\u0020\\u007F]+$");
const ALL_CONTROL_RE = new RegExp("[\\u0000-\\u0020\\u007F]", "g");

/**
 * Return a SAFE href, or `null` if the url must not become a live link.
 *
 * Allowlist (NOT denylist): only `http://`, `https://`, and a relative path
 * beginning with a single `/` survive. Everything else (javascript:, data:,
 * vbscript:, blob:, file:, mailto:, protocol-relative //host, a bare word, empty)
 * -> null.
 *
 * Obfuscation defenses BEFORE the scheme check:
 *   - strip leading/trailing whitespace + ASCII control chars (so `  javascript:`
 *     and `\tjavascript:` are caught);
 *   - drop EVERY control/space char anywhere up to (and past) the colon when
 *     forming the scheme-comparison view -- a real browser collapses
 *     `java\tscript:` / `java\nscript:` to `javascript:`;
 *   - compare case-insensitively (`JaVaScRiPt:` is `javascript:`).
 */
function sanitizeHref(rawUrl: string): string | null {
  if (typeof rawUrl !== "string") return null;

  // 1. Strip leading + trailing whitespace/control chars.
  const trimmed = rawUrl
    .replace(LEADING_CONTROL_RE, "")
    .replace(TRAILING_CONTROL_RE, "");
  if (trimmed.length === 0) return null;

  // 2. Control-stripped, lowercased view for scheme comparison (defeats a scheme
  //    split by embedded tab/newline/etc).
  const collapsed = trimmed.replace(ALL_CONTROL_RE, "").toLowerCase();
  if (collapsed.length === 0) return null;

  // 3. A relative path is safe (it can never carry a scheme) -- but only a SINGLE
  //    leading slash. `//host` is protocol-relative (off-site) and is rejected.
  if (collapsed.startsWith("/")) {
    if (collapsed.startsWith("//")) return null;
    return trimmed.replace(ALL_CONTROL_RE, "");
  }

  // 4. Explicit scheme allowlist on the collapsed form.
  if (collapsed.startsWith("http://") || collapsed.startsWith("https://")) {
    // Emit the control-stripped url so the href the browser sees matches the
    // scheme the allowlist actually approved (defense-in-depth).
    return trimmed.replace(ALL_CONTROL_RE, "");
  }

  // 5. Anything else (any other scheme, or a bare word with no scheme) -> inert.
  return null;
}
